"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Default base class for compiled templates.
 *
 * This class is an implementation detail of how template compilation currently
 * works, which might change. It should never be used directly. Use twig.load()
 * instead, which returns an instance of TwingTemplateWrapper.
 *
 * @author Fabien Potencier <fabien@symfony.com>
 *
 * @internal
 */
const runtime_1 = require("./error/runtime");
const source_1 = require("./source");
const error_1 = require("./error");
const template_wrapper_1 = require("./template-wrapper");
const output_buffering_1 = require("./output-buffering");
const iterator_to_map_1 = require("./helper/iterator-to-map");
const merge_1 = require("./helper/merge");
const context_1 = require("./context");
const is_map_1 = require("./helper/is-map");
class TwingTemplate {
    constructor(env) {
        this.parent = null;
        this.parents = new Map();
        this.blocks = new Map();
        this.traits = new Map();
        /**
         * @internal
         */
        this.extensions = new Map();
        this.env = env;
        this.extensions = env.getExtensions();
    }
    /**
     * @internal this method will be removed in 2.0 and is only used internally to provide an upgrade path from 1.x to 2.0
     */
    toString() {
        return this.getTemplateName();
    }
    /**
     * Returns information about the original template source code.
     *
     * @return TwingSource
     */
    getSourceContext() {
        return new source_1.TwingSource('', this.getTemplateName());
    }
    /**
     * Returns the parent template.
     *
     * @param context
     *
     * @returns TwingTemplate|TwingTemplateWrapper|false The parent template or false if there is no parent
     */
    getParent(context = {}) {
        if (this.parent !== null) {
            return this.parent;
        }
        let parent;
        try {
            parent = this.doGetParent(context);
            if (parent === false) {
                return false;
            }
            if (parent instanceof TwingTemplate || parent instanceof template_wrapper_1.TwingTemplateWrapper) {
                this.parents.set(parent.getSourceContext().getName(), parent);
            }
            if (!this.parents.has(parent)) {
                this.parents.set(parent, this.loadTemplate(parent));
            }
        }
        catch (e) {
            if (e instanceof error_1.TwingError) {
                e.setSourceContext(null);
            }
            throw e;
        }
        return this.parents.get(parent);
    }
    isTraitable() {
        return true;
    }
    /**
     * Displays a parent block.
     *
     * This method is for internal use only and should never be called
     * directly.
     *
     * @param {string} name The block name to display from the parent
     * @param context The context
     * @param {Map<string, TwingNodeBlock>} blocks The active set of blocks
     * @returns {string}
     *
     * @internal
     */
    displayParentBlock(name, context, blocks = new Map()) {
        let parent;
        if (this.traits.has(name)) {
            this.traits.get(name)[0].displayBlock(name, context, blocks, false);
        }
        else if ((parent = this.getParent(context)) !== false) {
            parent.displayBlock(name, context, blocks, false);
        }
        else {
            throw new runtime_1.TwingErrorRuntime(`The template has no parent and no traits defining the "${name}" block.`, -1, this.getSourceContext());
        }
    }
    /**
     * Displays a block.
     *
     * This method is for internal use only and should never be called
     * directly.
     *
     * @param {string} name The block name to display
     * @param context The context
     * @param {Map<string, Array<*>>} blocks The active set of blocks
     * @param {boolean} useBlocks Whether to use the active set of blocks
     *
     * @internal
     */
    displayBlock(name, context, blocks = new Map(), useBlocks = true) {
        let block;
        let template;
        let parent;
        if (useBlocks && blocks.has(name)) {
            template = blocks.get(name)[0];
            block = blocks.get(name)[1];
        }
        else if (this.blocks.has(name)) {
            template = this.blocks.get(name)[0];
            block = this.blocks.get(name)[1];
        }
        else {
            template = null;
            block = null;
        }
        if (template !== null) {
            Reflect.get(template, block).call(template, context, blocks);
        }
        else if ((parent = this.getParent(context)) !== false) {
            parent.displayBlock(name, context, merge_1.merge(this.blocks, blocks), false);
        }
        else if (blocks.has(name)) {
            throw new runtime_1.TwingErrorRuntime(`Block "${name}" should not call parent() in "${blocks.get(name)[0].getTemplateName()}" as the block does not exist in the parent template "${this.getTemplateName()}".`, -1, blocks.get(name)[0].getSourceContext());
        }
        else {
            throw new runtime_1.TwingErrorRuntime(`Block "${name}" on template "${this.getTemplateName()}" does not exist.`, -1, this.getSourceContext());
        }
    }
    /**
     * Renders a parent block.
     *
     * This method is for internal use only and should never be called
     * directly.
     *
     * @param {string} name The block name to display from the parent
     * @param {*} context The context
     * @param {Map<string, Array<any>>} blocks The active set of blocks
     *
     * @returns string The rendered block
     *
     * @internal
     */
    renderParentBlock(name, context, blocks = new Map()) {
        output_buffering_1.TwingOutputBuffering.obStart();
        this.displayParentBlock(name, context, blocks);
        return output_buffering_1.TwingOutputBuffering.obGetClean();
    }
    /**
     * Renders a block.
     *
     * This method is for internal use only and should never be called
     * directly.
     *
     * @param {string} name The block name to display
     * @param context The context
     * @param {Map<string, Array<*>>} blocks The active set of blocks
     * @param {boolean} useBlocks Whether to use the active set of blocks
     *
     * @return string The rendered block
     *
     * @internal
     */
    renderBlock(name, context, blocks = new Map(), useBlocks = true) {
        output_buffering_1.TwingOutputBuffering.obStart();
        this.displayBlock(name, context, blocks, useBlocks);
        return output_buffering_1.TwingOutputBuffering.obGetClean();
    }
    /**
     * Returns whether a block exists or not in the active context of the template.
     *
     * This method checks blocks defined in the active template
     * or defined in "used" traits or defined in parent templates.
     *
     * @param {string} name The block name
     * @param context The context
     * @param {Map<string, Array<*>>} blocks The active set of blocks
     *
     * @returns {boolean} true if the block exists, false otherwise
     */
    hasBlock(name, context, blocks = new Map()) {
        if (blocks.has(name)) {
            return blocks.get(name)[0] instanceof TwingTemplate;
        }
        if (this.blocks.has(name)) {
            return true;
        }
        let parent = this.getParent(context);
        if (parent) {
            return parent.hasBlock(name, context);
        }
        return false;
    }
    /**
     * Returns all block names in the active context of the template.
     *
     * This method checks blocks defined in the active template
     * or defined in "used" traits or defined in parent templates.
     *
     * @param context The context
     * @param {Map<string, Array<*>>} blocks The active set of blocks
     * @returns {Array<string>}
     */
    getBlockNames(context, blocks = new Map()) {
        let names = new Set([...blocks.keys(), ...this.blocks.keys()]);
        let parent = this.getParent(context);
        if (parent) {
            names = new Set([...names, ...parent.getBlockNames(context)]);
        }
        return [...names];
    }
    loadTemplate(template, templateName = null, line = null, index = null) {
        try {
            if (Array.isArray(template)) {
                return this.env.resolveTemplate(template, this.getSourceContext());
            }
            if (template instanceof TwingTemplate || template instanceof template_wrapper_1.TwingTemplateWrapper) {
                return template;
            }
            return this.env.loadTemplate(template, index, this.getSourceContext());
        }
        catch (e) {
            if (e instanceof error_1.TwingError) {
                if (!e.getSourceContext()) {
                    let source = this.getSourceContext();
                    e.setSourceContext(templateName ? new source_1.TwingSource(source.getCode(), templateName, source.getPath()) : source);
                }
                if (e.getTemplateLine() !== -1) {
                    throw e;
                }
                if (line) {
                    e.setTemplateLine(line);
                }
            }
            throw e;
        }
    }
    /**
     * Returns all blocks.
     *
     * This method is for internal use only and should never be called
     * directly.
     *
     * @returns {Map<string, Array<*>>} An array of blocks
     *
     * @internal
     */
    getBlocks() {
        return this.blocks;
    }
    display(context, blocks = new Map()) {
        if (context === null) {
            throw new TypeError('Argument 1 passed to TwingTemplate::display() must be an iterator, null given');
        }
        if (!is_map_1.isMap(context)) {
            context = iterator_to_map_1.iteratorToMap(context);
        }
        context = new context_1.TwingContext(this.env.mergeGlobals(context));
        this.displayWithErrorHandling(context, merge_1.merge(this.blocks, blocks));
    }
    render(context) {
        let level = output_buffering_1.TwingOutputBuffering.obGetLevel();
        output_buffering_1.TwingOutputBuffering.obStart();
        try {
            this.display(context);
        }
        catch (e) {
            while (output_buffering_1.TwingOutputBuffering.obGetLevel() > level) {
                output_buffering_1.TwingOutputBuffering.obEndClean();
            }
            throw e;
        }
        return output_buffering_1.TwingOutputBuffering.obGetClean();
    }
    doGetParent(context) {
        return false;
    }
    displayWithErrorHandling(context, blocks = new Map()) {
        try {
            this.doDisplay(context, blocks);
        }
        catch (e) {
            if (e instanceof error_1.TwingError) {
                if (!e.getSourceContext()) {
                    e.setSourceContext(this.getSourceContext());
                }
                throw e;
            }
            throw new runtime_1.TwingErrorRuntime(`An exception has been thrown during the rendering of a template ("${e.message}").`, -1, this.getSourceContext(), e);
        }
    }
    traceableMethod(method, lineno, source) {
        return function () {
            try {
                return method.apply(null, arguments);
            }
            catch (e) {
                if (e instanceof error_1.TwingError) {
                    if (e.getTemplateLine() === -1) {
                        e.setTemplateLine(lineno);
                        e.setSourceContext(source);
                    }
                }
                else {
                    e = new runtime_1.TwingErrorRuntime(`An exception has been thrown during the rendering of a template ("${e.message}").`, lineno, source, e);
                }
                throw e;
            }
        };
    }
    ;
    traceableDisplayBlock(lineno, source) {
        return this.traceableMethod(this.displayBlock.bind(this), lineno, source);
    }
    ;
    traceableDisplayParentBlock(lineno, source) {
        return this.traceableMethod(this.displayParentBlock.bind(this), lineno, source);
    }
    ;
    traceableRenderBlock(lineno, source) {
        return this.traceableMethod(this.renderBlock.bind(this), lineno, source);
    }
    traceableRenderParentBlock(lineno, source) {
        return this.traceableMethod(this.renderParentBlock.bind(this), lineno, source);
    }
    traceableHasBlock(lineno, source) {
        return this.traceableMethod(this.hasBlock.bind(this), lineno, source);
    }
}
exports.TwingTemplate = TwingTemplate;
TwingTemplate.ANY_CALL = 'any';
TwingTemplate.ARRAY_CALL = 'array';
TwingTemplate.METHOD_CALL = 'method';
